import React, { useState, useMemo, useEffect } from "react";
import {
  usePortsQuery,
  useDeletePortMutation,
} from "../../services/basicData/basicData";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Container } from "reactstrap";
import { Trans } from "react-i18next";
import ConfirmModal from "../../CommonElements/ConfirmModal";
import { useQueryClient } from "@tanstack/react-query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  DeleteIcon,
  Edit02Icon,
  AddSquareIcon,
  ArrowDataTransferDiagonalFreeIcons,
} from "@hugeicons/core-free-icons";
import { TableC } from "../../CommonElements/TableC";
import BtnAsLink from "../../Component/BtnAsLink";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";

/** Normalize ports list + meta from axios response (handles nested `data.data`). */
const portsPayloadFromAxios = (queryData) => {
  const body = queryData?.data;
  if (!body) return { rows: [], meta: {} };

  const inner = body.data;
  if (Array.isArray(inner)) {
    return { rows: inner, meta: body.meta ?? {} };
  }
  if (inner && Array.isArray(inner.data)) {
    return { rows: inner.data, meta: inner.meta ?? body.meta ?? {} };
  }
  return { rows: [], meta: body.meta ?? {} };
};

// Empty state component for when no ports are found
const EmptyState = ({ hasSearch }) => {
  const { t } = useTranslation();

  return (
    <div className="empty-state text-center py-5">
      <div className="empty-state-icon mb-3">
        <HugeiconsIcon
          icon={ArrowDataTransferDiagonalFreeIcons}
          size={48}
          className="text-muted"
        />
      </div>
      <h4 className="empty-state-title mb-2">{t("NO_PORTS_FOUND")}</h4>
      <p className="empty-state-description text-muted mb-4">
        {t("NO_PORTS_DESCRIPTION")}
      </p>
      {!hasSearch && (
        <Link to="create" className="btn btn-primary">
          <HugeiconsIcon
            icon={ArrowDataTransferDiagonalFreeIcons}
            size={16}
            className="me-2"
          />
          {t("CREATE_FIRST_PORT")}
        </Link>
      )}
    </div>
  );
};

const PAGE_SIZE = 10;

export const Ports = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModalState, setDeleteModalState] = useState({
    isOpen: false,
    portId: null,
  });
  const { searchValue, debouncedValue, setSearchValue } = useDebouncedSearch(
    "",
    500
  );

  const queryParams = useMemo(
    () => ({
      page: currentPage,
      per_page: PAGE_SIZE,
      ...(debouncedValue?.trim() ? { search: debouncedValue.trim() } : {}),
    }),
    [currentPage, debouncedValue]
  );

  const { data, isLoading, isFetching } = usePortsQuery(queryParams);
  const { rows: ports, meta } = portsPayloadFromAxios(data);
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const loading = isLoading || isFetching;

  const lastPage = Number(meta?.last_page);

  useEffect(() => {
    if (
      Number.isFinite(lastPage) &&
      lastPage >= 1 &&
      currentPage > lastPage
    ) {
      setCurrentPage(lastPage);
    }
  }, [lastPage, currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(1);
  };

  // Delete mutation
  const { mutate: deletePort, isPending: isDeleting } =
    useDeletePortMutation();

  // Handle delete modal
  const handleDeleteClick = (portId) => {
    setDeleteModalState({ isOpen: true, portId });
  };

  const handleDeleteCancel = () => {
    setDeleteModalState({ isOpen: false, portId: null });
  };

  const handleDeleteConfirm = () => {
    if (deleteModalState.portId) {
      deletePort(deleteModalState.portId, {
        onSuccess: () => {
          setDeleteModalState({ isOpen: false, portId: null });
          queryClient.invalidateQueries({ queryKey: ["ports"] });
        },
      });
    }
  };

  // Define columns configuration
  const columns = useMemo(
    () => [
      {
        name: t("NAME"),
        key: "name_ar",
        filterable: true,
      },
      {
        name: t("NAME_EN"),
        key: "name_en",
        filterable: true,
      },
      {
        name: t("CODE"),
        key: "code",
        filterable: true,
      },
      { name: t("ACTIONS"), key: "actions", filterable: false },
    ],
    [t]
  );

  // Define actions function
  const getActions = (rowData) => {
    return [
      {
        key: "edit",
        label: (
          <Link
            to={`/app/makkah/basic-data/ports/${rowData.id}/edit`}
            className="d-flex align-items-center gap-2"
          >
            <HugeiconsIcon size={16} icon={Edit02Icon} />
            {t("EDIT")}
          </Link>
        ),
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: (
          <div className="d-flex align-items-center gap-2">
            <HugeiconsIcon size={16} icon={DeleteIcon} />
            {t("DELETE")}
          </div>
        ),
        onClick: () => handleDeleteClick(rowData.id),
        danger: true,
      },
    ];
  };

  // Create button component
  const createButton = (
    <BtnAsLink
      className="btn-primary"
      icon={<HugeiconsIcon icon={AddSquareIcon} />}
      link="create"
      disabled={loading}
    >
      {t("CREATE_PORT")}
    </BtnAsLink>
  );

  return (
    <Container fluid>
      <TableC
        data={ports}
        columns={columns}
        loading={loading}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}
        searchPlaceholder={t("SEARCH_HERE")}
        pagination={
          meta?.total != null
            ? {
                current: currentPage,
                total: meta.total,
                pageSize: meta.per_page ?? PAGE_SIZE,
                onChange: handlePageChange,
              }
            : undefined
        }
        actions={getActions}
        renderActions={createButton}
        emptyState={EmptyState}
        minVisibleColumns={2}
        skeletonCount={3}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteModalState.isOpen}
        onToggle={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={t("CONFIRM")}
        message={
          <Trans
            i18nKey="CONFIRM_DELETE_PORT"
            components={{
              danger: <span className="text-danger" />,
            }}
          />
        }
        dangerText={t("DELETE")}
        confirmText={t("DELETE")}
        cancelText={t("CANCEL")}
        isLoading={isDeleting}
        defaultLoadingText={t("DELETING")}
        loadingText={t("DELETING")}
        confirmVariant="danger"
        confirmIcon={DeleteIcon}
      />
    </Container>
  );
};

